/**
 * The counties the agent works. Every list, Today, the mail run and the call session show
 * only these; the header switcher and the Counties page change them, and at least one
 * always stays on.
 */
import { ChevronDownIcon, MapPinIcon, SearchIcon } from "lucide-react";
import * as React from "react";
import { NavLink } from "react-router";
import type { County } from "@/api/models";
import { useWorkedCounties } from "@/api/queries";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { byState, countyName, matchStates, switcherName, useToggleCounty, workedLabel } from "@/lib/counties";
import { cn } from "@/lib/utils";

function CountyChoice({ county, onToggle, locked }: { county: County; onToggle: (on: boolean) => void; locked: boolean }) {
  const id = `switch-${county.key.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <li className="flex items-center gap-3 py-1.5">
      <Checkbox
        id={id}
        checked={county.selected}
        disabled={locked}
        onCheckedChange={(value) => onToggle(value === true)}
        aria-label={county.display}
        aria-describedby={locked ? `${id}-locked` : undefined}
      />
      <label htmlFor={id} className={cn("flex min-w-0 flex-1 items-baseline justify-between gap-3", locked ? "cursor-default" : "cursor-pointer")}>
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold text-heading">{county.name} County</span>
          {locked ? (
            <span className="text-xs text-muted" id={`${id}-locked`}>
              At least one county stays on
            </span>
          ) : null}
        </span>
        <span className="flex-none text-xs tabular-nums text-muted">
          {county.leads.toLocaleString("en-US")} {county.leads === 1 ? "lead" : "leads"}
        </span>
      </label>
    </li>
  );
}

/** Past this many counties the switcher offers a search. */
const SEARCH_FROM = 8;

/** The header's county switcher: shown once more than one county is live. */
export function CountySwitcher() {
  const { all, worked } = useWorkedCounties();
  const { toggle, isLast } = useToggleCounty();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const states = matchStates(byState(all), search);
  if (all.length < 2) return null;
  return (
    <Popover
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (!value) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2 max-md:hidden" id="county-switcher" aria-label={switcherName(worked)}>
          <MapPinIcon aria-hidden />
          <span className="max-w-40 truncate">{workedLabel(worked)}</span>
          <ChevronDownIcon aria-hidden className="text-muted" />
        </Button>
      </PopoverTrigger>
      {/* The list scrolls inside the panel, which never runs past the window (it once did, with ten counties). */}
      <PopoverContent
        align="end"
        collisionPadding={12}
        className="flex max-h-[min(36rem,var(--radix-popover-content-available-height))] w-88 flex-col p-0"
        aria-label="Your counties"
      >
        <div className="flex flex-col gap-1 border-b border-border px-4 pb-3 pt-4">
          <p className="m-0 font-display text-lg leading-snug text-heading">Your counties</p>
          <p className="m-0 text-xs text-muted">Every list shows only the counties you work.</p>
          {all.length > SEARCH_FROM ? (
            <div className="relative mt-2">
              <SearchIcon aria-hidden className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Find a county or state"
                aria-label="Find a county or state"
                className="ps-9"
                id="county-search"
              />
            </div>
          ) : null}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-2" id="county-choices" tabIndex={-1}>
          {states.map((state) => (
            <section key={state.code} aria-labelledby={`switch-state-${state.code}`} className="py-1">
              <h3 id={`switch-state-${state.code}`} className="sticky top-0 z-1 m-0 -mx-4 bg-popover px-4 py-1.5 text-xs font-semibold text-muted">
                {state.name}
              </h3>
              <ul className="m-0 list-none p-0">
                {state.counties.map((county) => (
                  <CountyChoice key={county.key} county={county} locked={isLast(county)} onToggle={(on) => toggle(county, on)} />
                ))}
              </ul>
            </section>
          ))}
          {states.length === 0 ? <p className="m-0 py-4 text-center text-sm text-muted">No county or state matches “{search.trim()}”.</p> : null}
        </div>
        <div className="flex items-center justify-between border-t border-border px-4 py-3">
          <span className="text-xs text-muted" aria-live="polite">
            {worked.length} of {all.length} on
          </span>
          <NavLink to="/counties" onClick={() => setOpen(false)} className="text-sm text-link underline-offset-2 hover:underline" id="manage-counties">
            Manage counties
          </NavLink>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** The county a lead row is in, shown when the list holds more than one county. */
export function CountyTag({ county }: { county: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-normal text-muted" data-testid="row-county">
      <MapPinIcon aria-hidden className="size-3" />
      {countyName(county)}
    </span>
  );
}
